// Firebase initialization and helpers
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  getDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  updateDoc,
  serverTimestamp,
  where,
  arrayUnion,
  getDocs,
  arrayRemove,
  deleteDoc,
  runTransaction,
  setDoc
} from 'firebase/firestore';
import { getStorage, ref as storageRef, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { GoogleAuthProvider, signInWithPopup, getAuth, signInAnonymously, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';

// Use environment variables for Firebase config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const storage = getStorage(app);

// Helpers
export async function anonymousSignIn() {
  try {
    const res = await signInAnonymously(auth);
    return res.user;
  } catch (err) {
    console.error('Anonymous sign-in failed', err);
    throw err;
  }
}

// Admin auth (email/password)
export async function signInAdmin(email, password) {
  try {
    const res = await signInWithEmailAndPassword(auth, email, password);
    return res.user;
  } catch (err) {
    console.error('Admin sign-in failed', err);
    throw err;
  }
}

export async function signOutAdmin() {
  try {
    await signOut(auth);
  } catch (err) {
    console.error('Sign out failed', err);
    throw err;
  }
}

export function onAuthChange(cb) {
  return onAuthStateChanged(auth, cb);
}

export function sitesCollection() {
  return collection(db, 'sites');
}

export async function addSite(site) {
  const col = sitesCollection();
  const docRef = await addDoc(col, { ...site, createdAt: serverTimestamp() });
  return docRef.id;
}

export async function updateSite(siteId, data) {
  const d = doc(db, 'sites', siteId);
  await updateDoc(d, data);
}

export function listenSites(onChange) {
  const q = query(collection(db, 'sites'), orderBy('createdAt'));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    onChange(items);
  });
}

export function teamsCollection() {
  return collection(db, 'teams');
}

export async function createTeam(team) {
  // Enforce uniqueness by using lcName as document id inside a transaction
  const lcName = (team.name || '').trim().toLowerCase();
  if (!lcName) throw new Error('Invalid team name');
  const teamRef = doc(db, 'teams', lcName);
  const base = { ...team, name: (team.name||'').trim(), lcName, createdAt: serverTimestamp(), points: 0 };
  // support passing an initialMember object { uid, name } and store members as a map { uid: name }
  if (team.initialMember) base.members = { [team.initialMember.uid]: (team.initialMember.name || '') };

  await runTransaction(db, async (t) => {
    const snap = await t.get(teamRef);
    if (snap.exists()) {
      throw new Error('Team already exists');
    }
    t.set(teamRef, base);
  });

  return lcName;
}

export async function getTeamByName(name) {
  const lc = (name || '').trim().toLowerCase();
  if (!lc) return null;
  const teamRef = doc(db, 'teams', lc);
  const snap = await getDoc(teamRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function addMemberToTeam(teamId, userId, userName) {
  if (!teamId || !userId) return;
  const teamRef = doc(db, 'teams', teamId);
  // Store members as map keyed by uid: { [uid]: name }
  await runTransaction(db, async (t) => {
    const snap = await t.get(teamRef);
    if (!snap.exists()) return;
    const update = {};
    update[`members.${userId}`] = userName || '';
    t.update(teamRef, update);
  });
}
// Task helpers: tasks are stored as subcollection `sites/{siteId}/tasks`
export async function addTaskToSite(siteId, task) {
  const col = collection(db, 'sites', siteId, 'tasks');
  const docRef = await addDoc(col, { title: task.title || '', description: task.description || '', type: task.type || 'text', points: typeof task.points === 'number' ? task.points : (task.points ? Number(task.points) : 10), createdAt: serverTimestamp() });
  return { id: docRef.id, title: task.title || '', description: task.description || '', type: task.type || 'text', points: typeof task.points === 'number' ? task.points : (task.points ? Number(task.points) : 10) };
}

export async function updateTaskInSite(siteId, taskId, patch) {
  const d = doc(db, 'sites', siteId, 'tasks', taskId);
  await updateDoc(d, { ...patch, updatedAt: serverTimestamp() });
}

export async function removeTaskFromSite(siteId, taskId) {
  const d = doc(db, 'sites', siteId, 'tasks', taskId);
  await deleteDoc(d);
}

export function listenSiteTasks(siteId, onChange) {
  if (!siteId) return () => {};
  const q = query(collection(db, 'sites', siteId, 'tasks'), orderBy('createdAt'));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    onChange(items);
  });
}

// Submissions filtered by team
export function listenTeamSubmissions(teamId, onChange) {
  if (!teamId) return () => {};
  const q = query(collection(db, 'submissions'), where('teamId', '==', teamId), orderBy('createdAt'));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    onChange(items);
  });
}

// Listen a single team document (for live updates to points, members, etc.)
export function listenTeam(teamId, onChange) {
  if (!teamId) return () => {};
  const d = doc(db, 'teams', teamId);
  return onSnapshot(d, (snap) => {
    onChange(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

export function listenTeams(onChange) {
  const q = query(collection(db, 'teams'), orderBy('points', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    onChange(items);
  });
}

export function submissionsCollection() {
  return collection(db, 'submissions');
}

export async function submitTask({teamId, siteId, taskId, payload}) {
  const col = submissionsCollection();
  const docRef = await addDoc(col, { teamId, siteId, taskId, payload, status: 'pending', createdAt: serverTimestamp() });
  return docRef.id;
}

// Player location helpers: store each player's last known location in `locations/{uid}`
export async function setPlayerLocation(uid, teamId, lat, lng, displayName) {
  if (!uid) return;
  const d = doc(db, 'locations', uid);
  const payload = { uid, teamId: teamId || null, lat, lng, updatedAt: serverTimestamp() };
  if (displayName) payload.displayName = displayName;
  await setDoc(d, payload, { merge: true });
  // also record to a history subcollection so we can draw movement trails later
  try {
    const historyCol = collection(db, 'locations', uid, 'history');
    await addDoc(historyCol, { uid, teamId: teamId || null, lat, lng, ts: serverTimestamp(), displayName: displayName || null });
  } catch (err) {
    console.warn('Failed to write location history', err);
  }
}

export function listenPlayerLocations(onChange) {
  const q = query(collection(db, 'locations'), orderBy('updatedAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    onChange(items);
  });
}

// Listen to a player's location history (subcollection `locations/{uid}/history`).
// Returns an unsubscribe function.
export function listenLocationHistory(uid, onChange, maxEntries = 500) {
  if (!uid) return () => {};
  const col = collection(db, 'locations', uid, 'history');
  const args = [col, orderBy('ts')];
  if (maxEntries && Number.isInteger(maxEntries)) args.push(limit(maxEntries));
  const q = query(...args);
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    onChange(items);
  });
}

// Start resumable image upload. Returns { task, promise } where promise resolves to download URL.
export function startImageUpload(file, teamId, siteId, taskId, onProgress) {
  if (!file) throw new Error('No file');
  const filename = `${teamId || 'anon'}_${siteId}_${taskId}_${Date.now()}_${file.name}`;
  const ref = storageRef(storage, `proofs/${filename}`);
  console.debug('startImageUpload -> ref path:', ref.fullPath || `proofs/${filename}`);
  const task = uploadBytesResumable(ref, file);

  const promise = new Promise((resolve, reject) => {
    task.on('state_changed', snapshot => {
      const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      if (onProgress) onProgress(pct);
    }, (err) => {
      console.error('upload state error', err);
      reject(err);
    }, async () => {
      try {
        const url = await getDownloadURL(task.snapshot.ref);
        if (onProgress) onProgress(100);
        resolve(url);
      } catch (err) {
        console.error('getDownloadURL error', err);
        reject(err);
      }
    });
  });

  return { task, promise };
}

export async function uploadProofImage(file, teamId, siteId, taskId) {
  const { promise } = startImageUpload(file, teamId, siteId, taskId);
  return promise;
}

// Google sign-in for admin
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  try {
    const res = await signInWithPopup(auth, provider);
    return res.user;
  } catch (err) {
    console.error('Google sign-in failed', err);
    throw err;
  }
}

export function listenSubmissions(onChange) {
  const q = query(collection(db, 'submissions'), orderBy('createdAt'));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    onChange(items);
  });
}

export async function approveSubmission(submissionId) {
  const subRef = doc(db, 'submissions', submissionId);
  const subSnap = await getDoc(subRef);
  if (!subSnap.exists()) throw new Error('Submission not found');
  const sub = subSnap.data();
  await updateDoc(subRef, { status: 'approved', reviewedAt: serverTimestamp() });

  // determine points for the task (read from task document); fall back to 10
  let points = 10;
  try {
    const taskRef = doc(db, 'sites', sub.siteId, 'tasks', sub.taskId);
    const taskSnap = await getDoc(taskRef);
    if (taskSnap.exists()) {
      const t = taskSnap.data();
      if (typeof t.points === 'number') points = t.points;
      else if (t.points) points = Number(t.points) || 10;
    }
  } catch (err) {
    console.warn('Failed to read task points, falling back to default', err);
  }

  // add points to the team
  const teamRef = doc(db, 'teams', sub.teamId);
  const teamSnap = await getDoc(teamRef);
  if (teamSnap.exists()) {
    const team = teamSnap.data();
    const newPoints = (team.points || 0) + points;
    await updateDoc(teamRef, { points: newPoints });
  }

  // After approval, check whether ALL tasks for the site are approved for this team.
  try {
    const tasksSnap = await getDocs(collection(db, 'sites', sub.siteId, 'tasks'));
    const tasks = tasksSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // fetch submissions for this team & site
    const subsQ = query(collection(db, 'submissions'), where('teamId', '==', sub.teamId), where('siteId', '==', sub.siteId));
    const subsSnap = await getDocs(subsQ);
    const subs = subsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const allApproved = tasks.length > 0 && tasks.every(t => subs.some(su => su.taskId === t.id && su.status === 'approved'));
    if (allApproved) {
      // mark site as completed for the team
      await updateDoc(teamRef, { completedSites: arrayUnion(sub.siteId) });
    }
  } catch (err) {
    console.error('Failed to update completedSites after approval', err);
  }
}

export async function rejectSubmission(submissionId, penalty = 5) {
  const subRef = doc(db, 'submissions', submissionId);
  const subSnap = await getDoc(subRef);
  if (!subSnap.exists()) throw new Error('Submission not found');
  const sub = subSnap.data();
  await updateDoc(subRef, { status: 'rejected', reviewedAt: serverTimestamp() });

  // deduct points from the team
  const teamRef = doc(db, 'teams', sub.teamId);
  const teamSnap = await getDoc(teamRef);
  if (teamSnap.exists()) {
    const team = teamSnap.data();
    const newPoints = (team.points || 0) - penalty;
    await updateDoc(teamRef, { points: newPoints });
  }

  // If the site was previously marked completed for this team, re-check and remove flag if not all tasks are approved
  try {
    const tasksSnap = await getDocs(collection(db, 'sites', sub.siteId, 'tasks'));
    const tasks = tasksSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const subsQ = query(collection(db, 'submissions'), where('teamId', '==', sub.teamId), where('siteId', '==', sub.siteId));
    const subsSnap = await getDocs(subsQ);
    const subs = subsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const allApproved = tasks.length > 0 && tasks.every(t => subs.some(su => su.taskId === t.id && su.status === 'approved'));
    if (!allApproved) {
      // remove site from completedSites if present
      await updateDoc(teamRef, { completedSites: arrayRemove(sub.siteId) });
    }
  } catch (err) {
    console.error('Failed to update completedSites after rejection', err);
  }
}

export async function deleteSite(siteId) {
  if (!siteId) throw new Error('No site ID');
  const siteRef = doc(db, 'sites', siteId);
  await deleteDoc(siteRef);
}

const firebaseExports = { app, db, auth };
export default firebaseExports;
